// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import React from "react";
import { track } from "@vercel/analytics";
import { PublicClientApplication } from "@azure/msal-browser";
import * as microsoftTeams from "@microsoft/teams-js";
import { detect, fromNavigator } from "@lingui/detect-locale";
import { Hanko } from "@teamhanko/hanko-elements";

import { config } from "../Config";
import {
  getMyGraphUserDetails,
  getGlobalAuthenticatedClient,
  getTeamsAccessToken,
} from "./GraphMain";
import store from "./StoreRoot";

const hankoApi = process.env.REACT_APP_HANKO_API_URL;

let hanko;
export interface AuthComponentProps {
  error: any;
  isAuthenticated: boolean;
  isLoaded: boolean;
  isPublic: boolean;
  isPlayground: boolean;
  isTeams: boolean;
  isLocal: boolean;
  user: any;
  tenantId: string;
  detailUrl: string;
  login: Function;
  logout: Function;
  logoutHanko: Function;
  getAccessToken: Function;
  getPickerToken: Function;
  checkSharingAccessRights: Function;
  checkFileAccessRights: Function;
  checkSitesAccessRights: Function;
  checkConsentRights: Function;
  setError: Function;
  loginWithMagicLink: Function;
}

export interface AuthComponentState {
  language: string;
  loading: boolean;
}

interface AuthProviderState {
  error: any;
  isAuthenticated: boolean;
  isLoaded: boolean;
  isPublic: boolean;
  isTeams: boolean;
  isLocal: boolean;
  isPlayground: boolean;
  user: any;
  tenantId: string;
  detailUrl: string;
  isAuthenticating: boolean;
}

export default function withAuthProvider<
  T extends React.Component<AuthComponentProps>,
>(
  WrappedComponent: new (props: AuthComponentProps, context?: any) => T
): React.ComponentClass {
  return class extends React.Component<any, AuthProviderState> {
    private publicClientApplication: PublicClientApplication;

    constructor(props: any) {
      super(props);

      const url = window.location.hash ?? "";
      const isPublic =
        url.includes("pub_") ||
        url.includes("xr3b") ||
        url.includes("xr4b") ||
        url.includes("#/page/") ||
        url.includes("#/sharingwizard/");
      const isTeams = store.isTeams;
      const isLocal = url.includes("#/file"); // || url.includes("#/playground");

      // hide the menu if it is public an not loggedIn
      let hideHomeMenu =
        isPublic &&
        !isTeams &&
        !store.main.userMe &&
        store.main.homeMenuStyle === "large";
      if (hideHomeMenu) {
        store.main.toggleHomeMenu(false);
      }

      const isAuthenticating = true; // !isPublic && !isTeams;

      this.state = {
        error: null,
        isAuthenticated: false,
        isLoaded: isPublic,
        isPublic: isPublic,
        isTeams: isTeams,
        isLocal: isLocal,
        isPlayground: false,
        user: {},
        tenantId: null,
        detailUrl: url,
        isAuthenticating,
      };
      // no authentication for public
      if (isPublic === true) {
        // set a userId
        if (!store.main.userId) {
          let userId = store.main.getPublicUserId();
          store.main.setUserId(userId);
        }
        store.main.setIsInitialized(true);

        return;
      }
      // no authentication for teams
      if (isTeams === true) return;

      // =======================================
      // prepare for the azure authentication
      // =======================================
      this.publicClientApplication = store.workbooks.getClientApplication();
      this.getPickerToken = this.getPickerToken.bind(this);
      this.logoutHanko = this.logoutHanko.bind(this);
    }

    getTeamsUserInfo() {
      microsoftTeams.getContext((context) => {
        // store it
        store.teams.setTeamsContext(context);
        console.log("INFO Teams context: ", context);

        let userId = context.userObjectId;
        let userName = context.userPrincipalName;
        let tenantId = context.tid;
        let userEmail = context.upn ?? context.loginHint;
        let locale = context.locale ?? "en-US";
        let user = {
          displayName: userName,
          id: userId,
          email: userEmail,
          tenantId: tenantId,
          timeZone: "UTC",
          timeFormat: null,
          photo: null,
        };
        store.main.setUserTenantId(tenantId);

        // user me
        let userMe = {
          displayName: userName,
          mail: userEmail,
          id: userId,
          preferredLanguage: locale,
        };
        store.main.setMicrosoftUser(userMe);

        this.setState({
          isLoaded: true,
          isAuthenticated: true,
          isAuthenticating: false,
          user: user,
        });
        store.main.toggleHomeMenu(true);
      });
    }

    componentDidMount() {
      // no login for public
      if (this.state.isPublic === true || this.state.isPlayground === true) {
        // check the language settings
        let tempLanguage = detect(fromNavigator(), "en-US");
        if (tempLanguage.includes("de")) store.main.setUserLanguage("de-DE");
        if (tempLanguage.includes("fr")) store.main.setUserLanguage("fr-FR");
        
        this.setState({ isAuthenticating: false });
        return;
      }

      if (this.state.isTeams) {
        try {
          microsoftTeams.initialize();

          // ==================================
          // TEAMS CONTEXT
          // ==================================
          let teamsContext = store.teams.teamsContext;
          if (!teamsContext) {
            this.getTeamsUserInfo();
          }

          // ==================================
          // TEAMS TOKEN
          // ==================================
          let teamsToken = store.teams.teamsSsoToken;
          if (!teamsToken) {
            microsoftTeams.authentication.getAuthToken({
              successCallback: (result: string) => {
                store.teams.setTeamsSsoToken(result);
                getTeamsAccessToken();
                // track
                store.main.withTracking &&
                  track("user-login-teams", {
                    type: "teams",
                  });
              },
              failureCallback: (error: string) => {
                console.log("ERROR getTeamsSsoToken", error);
              },
            });
          }
        } catch (err) {
          console.log("ERROR Teams Initialize", err);
        }
        this.setState({ isAuthenticating: false });
        return;
      }

      // If MSAL already has an account, the user
      // is already logged in
      this.publicClientApplication
        .handleRedirectPromise()
        .then(() => {
          const accounts = this.publicClientApplication.getAllAccounts();
          if (accounts && accounts.length > 0) {
            // Enhance user object with data from Graph
            this.getMicrosoftUserProfile();
          } else if (
            !this.state.isAuthenticated &&
            !store.main?.userEmail &&
            !store.main?.userId
          ) {
            this.getUserProfileFromMail();
          } else {
            this.setState({
              isLoaded: true,
              isAuthenticating: false,
            });
            const url = window.location.hash ?? "";
            // check if it is a detail url
            this.storeLastUrl(url);
          }
        })
        .catch((error) => {
          console.log("ERROR handleRedirectPromise", error);
          this.setState({
            isLoaded: true,
            isAuthenticating: false,
          });
        });
    }

    async login() {
      try {
        // define the needed scopes
        let scopes = config.scopes;
        const lastUrl = this.getLastUrl();

        // perhaps with file rights consent
        // or someone wants to open a workbook - need file rights
        const withFilesScope = store.main.getLocalStoreValue(
          "authWithFilesScope",
          false
        );
        if (withFilesScope === true || lastUrl.includes("/api?")) {
          scopes = ["user.read", "files.readwrite"]; //, "sites.readwrite.all"
          // delete the flag
          store.main.setLocalStoreValue("authWithFilesScope", null);
        }

        store.main.setUserScopes(scopes);

        // const isDesktopClient = (window.hasOwnProperty('todesktop'));
        // const isDesktopSafari = (store.isSafari && !store.isIOS);
        let usePopupLogin = this.state.isTeams ?? false;
        if (usePopupLogin) {
          await this.publicClientApplication?.loginPopup({
            scopes: scopes,
            prompt: "select_account",
          });
        } else {
          await this.publicClientApplication?.loginRedirect({
            scopes: scopes,
            prompt: "select_account",
          });
        }
        // After login, get the user's profile
        await this.getMicrosoftUserProfile();
      } catch (err) {
        console.log("ERROR AuthProvider", err);
        this.setState({
          isAuthenticated: false,
          isLoaded: true,
          user: {},
          error: this.normalizeError(err),
        });
      }
    }

    logout() {
      // this.userAgentApplication.logout();
      this.publicClientApplication.logout();
      store.main.setLocalStoreValue("userPhoto", null);
    }

    logoutHanko() {
      if (!hanko) hanko = new Hanko(hankoApi);

      hanko.user
        .logout()
        .then(() => {
          store.openUrl("/", true);
          // track
          store.main.withTracking &&
            track("user-logout", {
              type: "hanko",
            });
        })
        .catch((error) => {
          console.log(error);
        });
    }

    async getPickerToken(scopes: string[]): Promise<string> {
      const accounts = this.publicClientApplication.getAllAccounts();

      if (accounts.length <= 0) throw new Error("login_required");

      const silentResult =
        await this.publicClientApplication.acquireTokenSilent({
          scopes,
          account: accounts[0],
        });

      return silentResult.accessToken;
    }

    async getAccessToken(scopes: string[]): Promise<string> {
      this.setState({
        isAuthenticating: true,
      });
      if (store.isTeams) {
        return getTeamsAccessToken();
      }

      const needToRefresh = store.main.getLocalStoreValue(
        "AFTER_FILEACCESS_CONSENT",
        false
      );
      if (needToRefresh) {
        console.log("INFO Need to refresh access token");
      }

      try {
        const accounts = this.publicClientApplication.getAllAccounts();
        if (accounts.length <= 0) throw new Error("login_required");
        // Get the access token silently
        // If the cache contains a non-expired token, this function
        // will just return the cached token. Otherwise, it will
        // make a request to the Azure OAuth endpoint to get a token
        // var silentResult = await this.userAgentApplication.acquireTokenSilent({
        //   scopes: scopes,
        // });

        var silentResult;
        try {
          silentResult = await this.publicClientApplication.acquireTokenSilent({
            scopes: scopes,
            account: accounts[0],
            forceRefresh: needToRefresh,
          });
        } catch (err) {
          // If a silent request fails, it may be because the user needs
          // to login or grant consent to one or more of the requested scopes
          // if (this.isInteractionRequired(err)) {
          //   console.log("ERROR getAccessToken main", err);
          //   silentResult = await this.publicClientApplication.acquireTokenPopup(
          //     {
          //       scopes: scopes,
          //       account: accounts[0],
          //     }
          //   );
          // } else {
          //   throw err;
          // }
        }

        let expiresOn = silentResult?.expiresOn;
        console.log("INFO expires", expiresOn);
        store.setAccessToken(silentResult, "auth", "");
        // set the tenantId
        let tenantId = silentResult?.tenantId;
        this.setState({ tenantId: tenantId });
        store.main.setUserTenantId(tenantId);

        // check for file access
        const hasFileAccess = store.getHasUserFileAccess();
        if (hasFileAccess) {
          console.log("INFO app has file access");
        } else {
          console.log("INFO app has NO file access");
        }
        store.workbooks.setUserHasFileAccess(hasFileAccess);
        // delete the refresh flag
        if (needToRefresh) {
          store.main.setLocalStoreValue("AFTER_FILEACCESS_CONSENT", null);
          try {
            this.checkFileAccessRights();
          } catch (e) {
            console.log("ERROR checkFileAccessRights", e);
          }
        }
        await getGlobalAuthenticatedClient(
          silentResult?.accessToken,
          "auth",
          true
        );

        return silentResult?.accessToken;
      } catch (err) {
        this.setState({
          isAuthenticating: false,
        });
        throw err;
        // If a silent request fails, it may be because the user needs
        // to login or grant consent to one or more of the requested scopes
        // if (this.isInteractionRequired(err)) {
        //   console.log("ERROR getAccessTokenSilent new Token required", err);
        //   var interactiveResult = await this.publicClientApplication
        //     .acquireTokenPopup({
        //       scopes: scopes,
        //       prompt: "select_account"
        //     });

        //   return interactiveResult.accessToken;
        // } else {
        //   throw err;
        // }
      }
    }

    async checkSharingAccessRights(neededScope: string, scopesList: string[]) {
      // check actual consents
      let isOK = store.getIsAccessTokenScopeSupported(neededScope);
      if (isOK) {
        return true;
      }

      await this.publicClientApplication.handleRedirectPromise();
      const scopes = scopesList ?? [
        "user.readbasic.all",
        "mail.send",
        "files.readwrite.all",
        "team.readbasic.all",
        "channel.readbasic.all",
        "channelmessage.send",
        "chat.readbasic",
        "chatmessage.send",
      ];
      var interactiveResult =
        await this.publicClientApplication.acquireTokenRedirect({
          scopes: scopes,
          prompt: "select_account",
        });

      return interactiveResult;
    }

    async checkFileAccessRights() {
      // check actual consents
      let isOK =
        store.getIsAccessTokenScopeSupported("files.readwrite.all") ||
        store.getIsAccessTokenScopeSupported("files.readwrite");
      if (isOK) {
        return true;
      }

      await this.publicClientApplication.handleRedirectPromise();
      const scopes = ["files.readwrite.all"];
      var interactiveResult =
        await this.publicClientApplication.acquireTokenRedirect({
          scopes: scopes,
          prompt: "select_account",
        });

      return interactiveResult;
    }

    async checkSitesAccessRights() {
      // check actual consents
      let isOK = store.getIsAccessTokenScopeSupported("sites.readwrite.all");
      if (isOK) {
        return true;
      }

      await this.publicClientApplication.handleRedirectPromise();
      const scopes = ["sites.readwrite.all"];
      var interactiveResult =
        await this.publicClientApplication.acquireTokenRedirect({
          scopes: scopes,
          prompt: "select_account",
        });

      return interactiveResult;
    }

    async checkConsentRights(neededScope: string, scopesList: string[]) {
      if (scopesList.length === 0) return true;
      // check actual consents
      let isOK = store.getIsAccessTokenScopeSupported(neededScope);
      if (isOK) {
        return true;
      }

      await this.publicClientApplication.handleRedirectPromise();
      var interactiveResult =
        await this.publicClientApplication.acquireTokenRedirect({
          scopes: scopesList,
          prompt: "select_account",
        });

      return interactiveResult;
    }

    async storeLastUrl(url = "") {
      // check if it is a detail url
      if (url.length > 1 && url.length < 15) return;
      console.log("INFO storing last url");
      try {
        store.main.setLocalStoreValue("lastUrl", url.replace("#/", "/"));
      } catch (error) {
        console.log("ERROR storeLastUrl", error);
      }
    }

    getLastUrl() {
      let url = "";
      try {
        // check if it is a detail url
        url = store.main.getLocalStoreValue("lastUrl") ?? "";
        if (url.length < 15) url = "";
        if (url.includes("#/invitation") || url.includes("#/inbound")) url = "";
      } catch (error) {
        console.log("ERROR getLastUrl", error);
      }
      return url;
    }

    async getMicrosoftUserProfile() {
      try {
        let userScopes = store.main.userScopes ?? config.scopes;
        if (!userScopes) {
          userScopes = ["user.read"];
        }
        var accessToken = await this.getAccessToken(userScopes);
        if (accessToken) {
          // Get the user's profile from Graph
          var user = await getMyGraphUserDetails();
          this.setState({
            isAuthenticated: true,
            isLoaded: true,
            isAuthenticating: false,
            user: {
              displayName: user.user.displayName,
              id: user.user.id,
              email: user.user.mail || user.user.userPrincipalName,
              timeZone: user.user.mailboxSettings?.timeZone || "UTC",
              timeFormat: user.user.mailboxSettings?.timeFormat,
              photo: user.photo,
            },
            error: null,
          });
          // was there a stored url
          const lastUrl = this.getLastUrl();
          if (lastUrl.length > 15) {
            setTimeout(() => {
              console.log("INFO loading page from lastUrl", lastUrl);
              // this.storeLastUrl("");
              if (lastUrl.includes("/api?")) {
                store.workbooks.processApiParameters(lastUrl);
              } else {
                store.openUrl(lastUrl);
              }
            }, 500);
          }
        }
      } catch (err) {
        console.log("ERROR getMicrosoftUserProfile", err, user?.user);

        // if (err?.includes("InteractionRequiredAuthError")) {
        //   this.publicClientApplication
        //     .acquireTokenPopup(request)
        //     .then(function (response) {
        //       // call API
        //     })
        //     .catch(function (error) {
        //       console.log(error);
        //     });
        // }

        this.setState({
          isAuthenticated: false,
          isAuthenticating: false,
          isLoaded: true,
          user: {},
          error: this.normalizeError(err),
        });
      }
    }

    async getUserProfileFromMail() {
      try {
        if (!hanko) hanko = new Hanko(hankoApi);
        const user = await hanko.user?.getCurrent();
        if (user) {
          this.setState({
            isAuthenticated: true,
            isLoaded: true,
            isAuthenticating: false,
            user: {
              id: user.id,
              email: user.email,
              timeZone: "UTC",
            },
          });
          store.main.setEmailUser(user);
          // track
          store.main.withTracking &&
            track("user-login", {
              type: "hanko",
            });

          const lastUrl = this.getLastUrl();
          if (lastUrl.length > 15) {
            setTimeout(() => {
              console.log("INFO loading page from lastUrl", lastUrl);
              // this.storeLastUrl("");
              if (lastUrl.includes("/api?")) {
                store.workbooks.processApiParameters(lastUrl);
              } else {
                store.openUrl(lastUrl);
              }
            }, 500);
          }
        }
      } catch (err) {
        console.log("INFO no email user profile");
        this.setState({
          isAuthenticated: false,
          isAuthenticating: false,
          isLoaded: true,
          user: {},
          error: this.normalizeError(err),
        });
      }
    }

    setErrorMessage(message: string, debug: string) {
      this.setState({
        error: { message: message, debug: debug },
      });
    }

    normalizeError(error: string | Error): any {
      var normalizedError = {};
      if (typeof error === "string") {
        var errParts = error.split("|");
        normalizedError =
          errParts.length > 1
            ? { message: errParts[1], debug: errParts[0] }
            : { message: error };
      } else {
        normalizedError = {
          message: error.message,
          debug: JSON.stringify(error),
        };
      }
      return normalizedError;
    }

    // isInteractionRequired(error: Error): boolean {
    //   if (error.message) {
    //     console.log("ERROR isInteractionRequired", error);
    //   }
    //   if (!error.message || error.message.length <= 0) {
    //     return false;
    //   }

    //   return (
    //     error.message.indexOf("consent_required") > -1 ||
    //     error.message.indexOf("interaction_required") > -1 ||
    //     error.message.indexOf("login_required") > -1 ||
    //     error.message.indexOf("no_account_in_silent_request") > -1 ||
    //     error.message.indexOf("renewal") > -1 ||
    //     error.message.indexOf("readwrite") > -1
    //     // error.message.indexOf("readwrite.all") > -1
    //   );
    // }

    async loginWithMagicLink() {
      await this.getUserProfileFromMail();
    }

    render() {
      const { isAuthenticating } = this.state;

      if (isAuthenticating) {
        return null;
      }

      return (
        <WrappedComponent
          error={this.state.error}
          isAuthenticated={this.state.isAuthenticated}
          isLoaded={this.state.isLoaded}
          isPublic={this.state.isPublic}
          isTeams={this.state.isTeams}
          isLocal={this.state.isLocal}
          isPlayground={this.state.isPlayground}
          user={this.state.user}
          tenantId={this.state.tenantId}
          detailUrl={this.state.detailUrl}
          login={() => this.login()}
          loginWithMagicLink={() => this.loginWithMagicLink()}
          logout={() => this.logout()}
          logoutHanko={this.logoutHanko}
          getAccessToken={(scopes: string[]) => this.getAccessToken(scopes)}
          getPickerToken={this.getPickerToken}
          checkSharingAccessRights={(
            neededScope: string,
            neededScopes: string[]
          ) => this.checkSharingAccessRights(neededScope, neededScopes)}
          checkFileAccessRights={() => this.checkFileAccessRights()}
          checkSitesAccessRights={() => this.checkSitesAccessRights()}
          checkConsentRights={(neededScope: string, neededScopes: string[]) =>
            this.checkConsentRights(neededScope, neededScopes)
          }
          setError={(message: string, debug: string) =>
            this.setErrorMessage(message, debug)
          }
          {...this.props}
        />
      );
    }
  };
}

// Line breaks are for legibility only.
// admin rights
// GET https://login.microsoftonline.com/{tenant}/adminconsent
// ?client_id=6731de76-14a6-49ae-97bc-6eba6914391e
// &state=12345
// &redirect_uri=https://localhost/myapp/permissions
